import approx from 'approximate-number';
import { useRouter } from 'found';
import { truncate } from 'lodash';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import EventsIcon from '@mui/icons-material/Event';
import LikedIcon from '@mui/icons-material/Favorite';
import LikeIcon from '@mui/icons-material/FavoriteBorder';
import ProductsIcon from '@mui/icons-material/ShoppingCart';
import CommunityIcon from '@mui/icons-material/Group';
import JoinIcon from '@mui/icons-material/PersonAdd';
import LeaveIcon from '@mui/icons-material/PersonOutline';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import strip from 'strip';
import { useAuthContext } from 'AuthContext';
import { Col, Row } from 'components/Grid';
import Page from 'containers/Page';
import PageMenu from 'containers/Page/Menu';
import PageMenuItem from 'containers/Page/MenuItem';
import useIsMobile from 'lib/useIsMobile';
import { colors } from '@mui/material';
import CommunityApplicableEventDialog from './ApplicableEventDialog';
import CommunityJoinDialog from './JoinDialog';
import CommunityLeaveDialog from './LeaveDialog';
import CommunityMeta from './Meta';

const styles = {
  chip: { fontWeight: '600', color: '#FFF', width: 70 },
};

const CommunityPage = ({
  children,
  community,
  title,
  viewer,
  handleLike,
  handleJoin,
}) => {
  const {
    handleIntercepted,
    interceptAction,
    showSignInDialog,
  } = useAuthContext();
  const mobile = useIsMobile();
  const { router } = useRouter();
  const [applicableDialogVisible, setApplicableDialogVisible] = useState(false);
  const [joinDialogVisible, setJoinDialogVisible] = useState(false);
  const [leaveDialogVisible, setLeaveDialogVisible] = useState(false);
  const [liking, setLiking] = useState(false);
  const [memberChanging, setMemberChanging] = useState(false);
  const [likeStatus, setLikeStatus] = useState({ liked: false, likeCount: 0 });
  const [getJoinData, setJoinData] = useState();

  useEffect(() => {
    setLikeStatus({ liked: community?.liked, likeCount: community?.likeCount });
  }, [community?.likeCount, community?.liked]);

  const metaTitle = useMemo(
    () => `Check out ${strip(community?.name)}'s community on Ripple Street!`,
    [community?.name],
  );

  const metaDescription = useMemo(
    () => `${
        community?.summary
          ? strip(community?.summary)
          : truncate(strip(community?.description), {
              length: 140,
              separator: ' ',
            })
      } #sponsored`,
    [community?.summary, community?.description],
  );

  const heroAssetUrl = Array.isArray(community?.heroPlayList?.asset)
    ? community?.heroPlayList?.asset[0]?.hlsPlaylistUrl
    : '';

  const links = useMemo(
    () => [
      {
        icon: <CommunityIcon />,
        label: 'Brand',
        path: `/communities/${community?.slug}`,
        skip: !mobile,
      },
      {
        icon: <EventsIcon />,
        label: 'Events',
        path: `/communities/${community?.slug}/events`,
      },
      {
        icon: <ProductsIcon />,
        label: 'Products',
        path: `/communities/${community?.slug}/products`,
      },
    ],
    [community?.slug, mobile],
  );

  const meta = useMemo(
    () => [
      {
        property: 'og:type',
        content: 'article',
      },
      {
        property: 'og:tag',
        content: 'sponsored',
      },
      {
        property: 'og:title',
        content: metaTitle,
      },
      {
        property: 'og:description',
        content: metaDescription,
      },
      heroAssetUrl
        ? {
            property: 'og:image',
            content: heroAssetUrl,
          }
        : {},
      {
        property: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        property: 'twitter:title',
        content: metaTitle,
      },
      {
        property: 'twitter:description',
        content: metaDescription,
      },
      heroAssetUrl
        ? {
            property: 'twitter:image',
            content: heroAssetUrl,
          }
        : {},
    ],
    [heroAssetUrl, metaDescription, metaTitle],
  );

  const titleLink = useMemo(() => `/communities/${community?.slug}`, [
    community?.slug,
  ]);

  const trackerData = useMemo(
    () => ({
      community,
      viewer,
    }),
    [community, viewer],
  );

  const trackerTitle = useMemo(
    () => `Communities > ${community?.name}${title ? ` > ${title}` : ''}`,
    [community?.name, title],
  );

  const hideApplicableDialog = useCallback(
    () => setApplicableDialogVisible(false),
    [setApplicableDialogVisible],
  );

  const hideJoinDialog = useCallback(() => setJoinDialogVisible(false), [
    setJoinDialogVisible,
  ]);

  const hideLeaveDialog = useCallback(() => setLeaveDialogVisible(false), [
    setLeaveDialogVisible,
  ]);

  const showApplicableDialog = useCallback(
    () => setApplicableDialogVisible(true),
    [setApplicableDialogVisible],
  );

  const showJoinDialog = useCallback(() => setJoinDialogVisible(true), [
    setJoinDialogVisible,
  ]);

  const showLeaveDialog = useCallback(() => setLeaveDialogVisible(true), [
    setLeaveDialogVisible,
  ]);

  const apply = useCallback(() => {
    if (Array.isArray(getJoinData) && getJoinData?.length > 0) {
      router.push(`/events/${getJoinData[0]?.slug}`);
    } else {
      router.push('/events');
    }
  }, [router, getJoinData]);

  const join = useCallback(() => {
    if (!community?.joinedCommunity && viewer?.loggedIn) {
      setMemberChanging(true);
      handleJoin({
        variables: {
          joinCommunity: true,
          id: community?.id,
        },
      }).then(res => {
        const getJoinRes = JSON.parse(res?.data?.joinOrLeaveSponsor);
        setJoinData(getJoinRes);
        setMemberChanging(false);
        hideJoinDialog();
        if (community) {
          showApplicableDialog();
        }
      });
    } else {
      showSignInDialog();
    }
  }, [
    community,
    community,
    hideJoinDialog,
    setMemberChanging,
    showApplicableDialog,
    handleJoin,
    showSignInDialog,
  ]);

  const leave = useCallback(() => {
    if (community?.joinedCommunity) {
      setMemberChanging(true);
      hideLeaveDialog();
      handleJoin({
        variables: {
          joinCommunity: false,
          id: community?.id,
        },
      }).then(() => {
        setMemberChanging(false);
      });
    }
  }, [community, hideLeaveDialog, setMemberChanging, handleJoin]);

  const like = useCallback(() => {
    setLiking(true);
    handleLike({
      variables: {
        isLiked: !likeStatus?.liked,
        id: community?.id,
      },
    }).then(likeRes => {
      if (likeRes?.data?.likeOrDislikeSponsor === true) {
        setLikeStatus({ liked: true, likeCount: likeStatus.likeCount + 1 });
      } else {
        setLikeStatus({ liked: false, likeCount: likeStatus.likeCount - 1 });
      }
      setLiking(false);
    });
  }, [likeStatus, community]);

  const onLike = useCallback(() => {
    if (viewer?.loggedIn) {
      like();
    } else {
      interceptAction(`like-${community?.id}`);
    }
  }, [community?.id, interceptAction, like, viewer?.loggedIn]);

  const memberChange = useCallback(() => {
    if (community?.joinedCommunity) {
      showLeaveDialog();
    } else {
      showJoinDialog();
    }
  }, [community?.joinedCommunity, showJoinDialog, showLeaveDialog]);

  const onMemberChange = useCallback(() => {
    if (viewer?.loggedIn) {
      memberChange();
    } else {
      interceptAction(`memberChange-${community?.id}`);
    }
  }, [community?.id, interceptAction, memberChange, viewer?.loggedIn]);

  const rightButton = useMemo(() => {
    if (community?.joinedCommunity) {
      return null;
    }
    return memberChanging ? (
      <CircularProgress size={32} />
    ) : (
      <Chip
        color='success'
        onClick={showJoinDialog}
        style={styles.chip}
        label='Join'
      />
    );
  }, [community?.joinedCommunity, memberChanging, showJoinDialog]);

  const memberMenu = useMemo(
    () => (
      <PageMenu>
        <PageMenuItem
          disabled={memberChanging}
          leftIcon={
            community?.joinedCommunity ? (
              <LeaveIcon htmlColor={colors.red[500]} />
            ) : (
              <JoinIcon htmlColor={colors.purple[300]} />
            )
          }
          onClick={onMemberChange}
          primaryTextColor={
            community?.joinedCommunity ? colors.red[500] : colors.purple[300]
          }
          primaryText={
            community?.joinedCommunity
              ? 'Leave this community'
              : 'Join this community'
          }
          style={community?.joinedCommunity ? {} : {}}
        />
      </PageMenu>
    ),
    [community?.joinedCommunity, memberChanging, onMemberChange],
  );

  const communityMeta = useMemo(
    () => (
      <CommunityMeta community={community} viewer={viewer}>
        {mobile && memberMenu}
      </CommunityMeta>
    ),
    [community, memberMenu, mobile, viewer],
  );

  const rightDrawer = useMemo(
    () => community?.joinedCommunity && communityMeta,
    [community?.joinedCommunity, communityMeta],
  );

  useEffect(() => {
    if (viewer?.loggedIn) {
      handleIntercepted(`like-${community?.id}`, like);
      handleIntercepted(`memberChange-${community?.id}`, memberChange);
    }
  }, [community?.id, handleIntercepted, like, memberChange, viewer?.loggedIn]);

  return (
    <Page
      links={links}
      meta={meta}
      rightButton={rightButton}
      rightDrawer={rightDrawer}
      title={community?.name}
      titleLink={titleLink}
      trackerData={trackerData}
      trackerTitle={trackerTitle}
      viewer={viewer}
    >
      <Row>
        <Col main>{children}</Col>
        <Col meta>
          {memberMenu}
          <PageMenu>
            <PageMenuItem
              disabled={liking}
              leftIcon={useMemo(
                () => (likeStatus?.liked ? (
                  <LikedIcon htmlColor={colors.red[500]} />
                  ) : (
                    <LikeIcon />
                  )),
                [likeStatus?.liked],
              )}
              onClick={onLike}
              primaryText={useMemo(
                () => `${approx(likeStatus?.likeCount || 0)} likes`,
                [likeStatus?.likeCount],
              )}
            />
          </PageMenu>
          {communityMeta}
        </Col>
      </Row>
      {community && (
        <CommunityApplicableEventDialog
          event={community}
          onCancel={hideApplicableDialog}
          onConfirm={apply}
          open={applicableDialogVisible}
        />
      )}
      {community?.joinedCommunity ? (
        <CommunityLeaveDialog
          onCancel={hideLeaveDialog}
          onConfirm={leave}
          open={leaveDialogVisible}
        />
      ) : (
        <CommunityJoinDialog
          community={community}
          onCancel={hideJoinDialog}
          onConfirm={join}
          open={joinDialogVisible}
        />
      )}
    </Page>
  );
};

CommunityPage.propTypes = {
  children: PropTypes.node.isRequired,
  community: PropTypes.object.isRequired,
  title: PropTypes.string,
  viewer: PropTypes.object.isRequired,
  handleLike: PropTypes.any.isRequired,
  handleJoin: PropTypes.any.isRequired,
};

export default CommunityPage;
